import React, { useState, useEffect } from 'react';

import {
  faCheckCircle,
  faTimesCircle,
  faTrash,
  faPencil,
  faCalendar,
  faGlobe,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Card,
  Badge,
  Group,
  Text,
  LoadingOverlay,
  Alert,
  Stack,
  Modal,
  Button
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import IntegrationForm from './IntegrationForm';
import { SYSTEM_CONFIGS, useGetIntegrations, type Integration } from '../../utils/integration';
import { PostJSON } from '../../utils/request';

interface SyncNowResponse {
  success: boolean;
  error?: string;
}

interface IntegrationListProps {
  onRefetchReady?: (refetch: () => void) => void;
}

const IntegrationList: React.FC<IntegrationListProps> = ({ onRefetchReady }) => {
  const [deletingIntegration, setDeletingIntegration] = useState<Integration | null>(null);
  const [syncingIntegration, setSyncingIntegration] = useState<Integration | null>(null);
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);

  const {
    data,
    error,
    loading: isLoading,
    refetch
  } = useGetIntegrations();

  useEffect(() => {
    if (onRefetchReady) {
      onRefetchReady(refetch);
    }
  }, [onRefetchReady, refetch]);

  const handleDeleteIntegration = (integration: Integration) => {
    setDeletingIntegration(integration);
  };

  const handleConfirmDelete = async () => {
    if (!deletingIntegration) return;

    try {
      await PostJSON('/api/integration/set_enabled', {
        id: deletingIntegration.id,
        isEnabled: false
      });
      
      setDeletingIntegration(null);
      refetch();
      
      notifications.show({
        title: 'Success',
        message: 'Integration has been disabled successfully.',
        color: 'green'
      });
    } catch (error) {
      console.error('Error disabling integration:', error);
      
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to disable integration. Please try again.',
        color: 'red'
      });
    }
  };

  const handleSyncIntegration = (integration: Integration) => {
    setSyncingIntegration(integration);
  };

  const handleConfirmSync = async () => {
    if (!syncingIntegration) return;

    try {
      const response = await PostJSON<SyncNowResponse>('/api/integration/run_sync', {
        id: syncingIntegration.id
      });
      
      if (!response.success) {
        setSyncingIntegration(null);
        notifications.show({
          title: 'Error',
          message: response.error || 'Failed to run sync. Please try again.',
          color: 'red'
        });
        return;
      }
      
      setSyncingIntegration(null);
      refetch();
      
      notifications.show({
        title: 'Success',
        message: 'Sync has been started successfully.',
        color: 'green'
      });
    } catch (error) {
      console.error('Error running sync:', error);
      
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to run sync. Please try again.',
        color: 'red'
      });
    }
  };

  const formatSystemName = (system: string): string => {
    if (SYSTEM_CONFIGS[system]) {
      return SYSTEM_CONFIGS[system].name;
    }
    return system
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (error) {
    return (
      <Alert color="red" title="Error">
        {error.message}
      </Alert>
    );
  }

  const integrations = data?.integrations || [];

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLoading} />

      {integrations.length === 0 ? (
        <Text c="dimmed" py="xl" ta="center">
          No integrations found.
        </Text>
      ) : (
        <Stack gap="md">
          {integrations.map((integration) => (
            <Card key={integration.id} withBorder p="md" shadow="sm">
              <Stack gap="xs">
                <Group gap="xs" justify="space-between">
                  <Group gap="xs">
                    <Text fw={600} size="lg">
                      {formatSystemName(integration.system)}
                    </Text>
                    <Badge
                      color={integration.isEnabled ? 'green' : 'red'}
                      leftSection={
                        <FontAwesomeIcon
                          icon={integration.isEnabled ? faCheckCircle : faTimesCircle}
                          size="xs"
                        />
                      }
                      size="sm"
                      variant="light"
                    >
                      {integration.isEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </Group>
                  <Group gap="xs">
                    {integration.type === 'record' && (
                      <Button
                        color="blue"
                        leftSection={<FontAwesomeIcon icon={faSync} size="sm" />}
                        size="sm"
                        variant="subtle"
                        onClick={() => handleSyncIntegration(integration)}
                      >
                        Sync Now
                      </Button>
                    )}
                    <Button
                      leftSection={<FontAwesomeIcon icon={faPencil} size="sm" />}
                      size="sm"
                      variant="subtle"
                      onClick={() => setEditingIntegration(integration)}
                    >
                      Edit
                    </Button>
                    <Button
                      color="red"
                      leftSection={<FontAwesomeIcon icon={faTrash} size="sm" />}
                      size="sm"
                      variant="subtle"
                      onClick={() => handleDeleteIntegration(integration)}
                    >
                      Disable
                    </Button>
                  </Group>
                </Group>

                {integration.metadata && (() => {
                  const systemConfig = SYSTEM_CONFIGS[integration.system];
                  if (!systemConfig) return null;

                  // Get non-sensitive metadata fields from the config
                  const textFields = systemConfig.metadata
                    .filter(field => !field.isSensitive && field.type !== 'boolean')
                    .filter(field => integration.metadata[field.key] !== undefined);
                  const booleanFields = systemConfig.metadata
                    .filter(field => field.type === 'boolean');

                  if (textFields.length === 0 && booleanFields.length === 0) return null;

                  return (
                    <Stack gap={4} mt="xs" pl="md">
                      {textFields.map((field) => (
                        <Group key={field.key} gap="xs">
                          <Text c="dimmed" fw={500} size="sm">
                            {field.label}:
                          </Text>
                          <Text size="sm">
                            {String(integration.metadata[field.key])}
                          </Text>
                        </Group>
                      ))}
                      {booleanFields.length > 0 && (
                        <Group gap="xs" mt={4}>
                          {booleanFields.map((field) => (
                            <Badge
                              key={field.key}
                              color={integration.metadata[field.key] ? 'green' : 'gray'}
                              size="sm"
                              variant="light"
                            >
                              {field.label}: {integration.metadata[field.key] ? 'On' : 'Off'}
                            </Badge>
                          ))}
                        </Group>
                      )}
                    </Stack>
                  );
                })()}

                {integration.lastSyncResults && (() => {
                  const results = integration.lastSyncResults;
                  const resultItems: Array<{ label: string; value: number }> = [];
                  
                  if (results.nClients !== undefined) {
                    resultItems.push({ label: 'Clients', value: results.nClients });
                  }
                  if (results.nCallers !== undefined) {
                    resultItems.push({ label: 'Callers Matched', value: results.nCallers });
                  }
                  if (results.nVisits !== undefined) {
                    resultItems.push({ label: 'Visits Updated', value: results.nVisits });
                  }
                  if (results.nAutoAgents !== undefined) {
                    resultItems.push({ label: 'Auto Agents Queued', value: results.nAutoAgents });
                  }

                  if (resultItems.length === 0) return null;

                  return (
                    <Stack gap={4} mt="xs" pl="md">
                      <Text c="dimmed" fw={500} size="sm">
                        Last Sync Results:
                      </Text>
                      {resultItems.map((item) => (
                        <Group key={item.label} gap="xs" pl="md">
                          <Text c="dimmed" size="sm">
                            {item.label}:
                          </Text>
                          <Text size="sm">
                            {item.value}
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  );
                })()}

                <Group gap="md" mt="xs" wrap="wrap">
                  <Group gap="xs">
                    <FontAwesomeIcon icon={faGlobe} size="sm" />
                    <Text size="sm">{integration.tz}</Text>
                  </Group>
                  {integration.lastSyncAt && (
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faCalendar} size="sm" />
                      <Text c="dimmed" size="sm">
                        Last Sync: {new Date(integration.lastSyncAt).toLocaleString()}
                      </Text>
                    </Group>
                  )}
                  {integration.nextSyncAt && (
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faCalendar} size="sm" />
                      <Text c="dimmed" size="sm">
                        Next Sync: {new Date(integration.nextSyncAt).toLocaleString()}
                      </Text>
                    </Group>
                  )}
                </Group>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      {/* Disable Confirmation Modal */}
      <Modal
        opened={!!deletingIntegration}
        size="sm"
        title="Disable Integration"
        onClose={() => setDeletingIntegration(null)}
      >
        {deletingIntegration && (
          <Stack gap="md">
            <Text>
              Are you sure you want to disable the integration for&nbsp;
              <Text component="span" fw={700}>
                {formatSystemName(deletingIntegration.system)}
              </Text>
              ? If you want to re-enable it, you will need to provide the credentials again.
            </Text>
            <Group gap="sm" justify="flex-end">
              <Button variant="outline" onClick={() => setDeletingIntegration(null)}>
                Cancel
              </Button>
              <Button color="red" onClick={() => void handleConfirmDelete()}>
                Disable
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Sync Confirmation Modal */}
      <Modal
        opened={!!syncingIntegration}
        size="sm"
        title="Sync Integration"
        onClose={() => setSyncingIntegration(null)}
      >
        {syncingIntegration && (
          <Stack gap="md">
            <Text>
              Are you sure you want to run a sync now for&nbsp;
              <Text component="span" fw={700}>
                {formatSystemName(syncingIntegration.system)}
              </Text>
              ? This will start the sync process immediately.
            </Text>
            <Group gap="sm" justify="flex-end">
              <Button variant="outline" onClick={() => setSyncingIntegration(null)}>
                Cancel
              </Button>
              <Button color="blue" onClick={() => void handleConfirmSync()}>
                Sync Now
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Edit Integration Modal */}
      <Modal
        opened={!!editingIntegration}
        title={`Edit ${editingIntegration ? formatSystemName(editingIntegration.system) : ''}`}
        onClose={() => setEditingIntegration(null)}
      >
        {editingIntegration && (
          <IntegrationForm
            integration={editingIntegration}
            onCancel={() => setEditingIntegration(null)}
            onSuccess={() => {
              setEditingIntegration(null);
              refetch();
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default IntegrationList;

